import { DateTimeFormatter, Duration, Instant, ZoneId } from "@js-joda/core";
import { Locale } from "@js-joda/locale_en-us";
import { orderBy } from "lodash";
import { ComponentPropsWithoutRef } from "react";
import { ClinicalNote } from "../../model/ClinicalNote";
import cn from "../../util/cn";

type ClinicalNotesListProps = {
  clinicalNotes: ClinicalNote[];
  filterOptionsForPrinting: {
    timeRangeStart: Instant;
    timeRangeEnd: Instant;
  };
} & ComponentPropsWithoutRef<"div">;

const ClinicalNotesList = ({
  clinicalNotes,
  filterOptionsForPrinting,
  className,
  ...otherProps
}: ClinicalNotesListProps) => {
  const { timeRangeStart, timeRangeEnd } = filterOptionsForPrinting;
  const sortedClinicalNotes = orderBy(clinicalNotes, (it) => it.dateTime.toEpochMilli(), ["desc"]);
  return (
    <div className={className} {...otherProps}>
      <div className="flex flex-col gap-4">
        {sortedClinicalNotes.map((clinicalNote) => (
          <div
            key={clinicalNote.id}
            className={cn(
              "flex w-full flex-col gap-2 overflow-hidden rounded-md border border-black/15 bg-gray-50 p-4 print:break-inside-avoid-page",
              clinicalNote.dateTime.isBefore(timeRangeStart) ||
                clinicalNote.dateTime.isAfter(timeRangeEnd) ||
                clinicalNote.dateTime.equals(timeRangeEnd)
                ? "print:hidden"
                : ""
            )}
          >
            <div className="flex flex-col">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center">
                  <span className="me-3 truncate font-bold text-primary/90">
                    {clinicalNote.creatorName}
                  </span>
                  <span className="truncate text-sm text-primary/60">Time spent:</span>
                  <span className="ms-1 truncate text-sm text-primary/90">
                    {formatAsHumanReadableMinutes(clinicalNote.timeSpent)}
                  </span>
                  {clinicalNote.livePatientInteraction && (
                    <>
                      <Dot className="mx-2" />
                      <span className="truncate text-sm text-primary/60">Live interaction</span>
                    </>
                  )}
                </div>
                <span className="truncate text-sm text-primary/60">
                  {dateTimeFormatter.format(clinicalNote.dateTime.atZone(ZoneId.SYSTEM))}
                </span>
              </div>
              <div className="mt-1 flex items-center text-sm"></div>
              <div className="mt-2">
                <span className="text-primary/90">{clinicalNote.notes}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

type DotProps = ComponentPropsWithoutRef<"span">;

const Dot = ({ className, ...otherProps }: DotProps) => (
  <span className={cn("size-[6px] truncate rounded-full bg-gray-300", className)} {...otherProps} />
);

const formatAsHumanReadableMinutes = (duration: Duration) => {
  const roundedMinutes = Math.ceil(duration.seconds() / 60);
  return `${roundedMinutes} min`;
};

const dateTimeFormatter = DateTimeFormatter.ofPattern("MMM dd, yyyy 'at' h:mm a").withLocale(
  Locale.US
);

export default ClinicalNotesList;
